import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  VStack,
  Box,
} from "@chakra-ui/react";
import { useState, useEffect } from "react";
import { ChecklistItemViewModel } from "./types";
import { useCategories } from "hooks/useCategories";
import CreatableSelect from "react-select/creatable";
import { ChakraStylesConfig } from "chakra-react-select";
import { GroupBase } from "react-select";

interface CategoryOption {
  label: string;
  value: string;
}

interface ChecklistItemModalProps {
  checklistItem: ChecklistItemViewModel;
  isOpen: boolean;
  onClose: () => void;
  onAdd: (checklistItem: ChecklistItemViewModel) => void;
  onUpdate: (checklistItem: ChecklistItemViewModel) => void;
  loan_type: string;
}

const categorySelectStyles: ChakraStylesConfig<CategoryOption, false, GroupBase<CategoryOption>> = {
  control: base => ({
    ...base,
    borderColor: "gray.200",
    _hover: {
      borderColor: "gray.300",
    },
    _focus: {
      borderColor: "blue.500",
      boxShadow: "none",
    },
  }),
  menu: base => ({
    ...base,
    backgroundColor: "white",
    border: "1px solid",
    borderColor: "gray.200",
    borderRadius: "md",
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isFocused ? "blue.50" : "transparent",
    color: "gray.800",
    _hover: {
      backgroundColor: "blue.50",
    },
  }),
  container: base => ({
    ...base,
    width: "100%",
  }),
  valueContainer: base => ({
    ...base,
    padding: "0 8px",
  }),
  input: base => ({
    ...base,
    color: "gray.800",
  }),
  placeholder: base => ({
    ...base,
    color: "gray.500",
  }),
  singleValue: base => ({
    ...base,
    color: "gray.800",
  }),
  dropdownIndicator: base => ({
    ...base,
    color: "gray.500",
  }),
  clearIndicator: base => ({
    ...base,
    color: "gray.500",
  }),
  multiValue: base => ({
    ...base,
    backgroundColor: "blue.50",
    color: "blue.700",
  }),
  multiValueLabel: base => ({
    ...base,
    color: "blue.700",
  }),
  multiValueRemove: base => ({
    ...base,
    color: "blue.700",
    _hover: {
      backgroundColor: "blue.100",
    },
  }),
};

export function ChecklistItemModal({
  checklistItem,
  isOpen,
  onClose,
  onAdd,
  onUpdate,
  loan_type,
}: ChecklistItemModalProps) {
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("");
  const [description, setDescription] = useState("");
  const [order, setOrder] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const { categories, addCategory } = useCategories();

  useEffect(() => {
    if (checklistItem) {
      setTitle(checklistItem.title);
      setCategory(checklistItem.category);
      setDescription(checklistItem.description);
      setOrder(checklistItem.order || 0);
    }
  }, [checklistItem]);

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      const updatedItem = {
        ...checklistItem,
        title,
        category,
        description,
        order,
        assignments: checklistItem.assignments || [],
      };

      if (checklistItem.id === 0) {
        await onAdd(updatedItem);
      } else {
        await onUpdate(updatedItem);
      }
      onClose();
    } finally {
      setIsLoading(false);
    }
  };

  const categoryOptions: CategoryOption[] =
    categories?.map(cat => ({
      label: cat,
      value: cat,
    })) || [];

  const selectedCategory = category ? { label: category, value: category } : null;

  const handleCreateOption = async (inputValue: string) => {
    try {
      await addCategory({ category: inputValue, loan_type });
      setCategory(inputValue);
    } catch (error) {
      // Error handling is done by the mutation
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{checklistItem.id ? "Edit Checklist Item" : "New Checklist Item"}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4}>
            <FormControl isRequired>
              <FormLabel>Title</FormLabel>
              <Input
                value={title}
                onChange={e => setTitle(e.target.value)}
                placeholder="Enter checklist item title"
              />
            </FormControl>
            <FormControl isRequired>
              <FormLabel>Category</FormLabel>
              <Box>
                <CreatableSelect<CategoryOption>
                  isClearable
                  value={selectedCategory}
                  onChange={newValue => {
                    setCategory(newValue?.value || "");
                  }}
                  onCreateOption={handleCreateOption}
                  options={categoryOptions}
                  placeholder="Select or create a category..."
                  chakraStyles={categorySelectStyles}
                  components={{
                    ClearIndicator: () => null,
                  }}
                />
              </Box>
            </FormControl>
            <FormControl>
              <FormLabel>Description</FormLabel>
              <Textarea
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder="Enter description"
                rows={4}
              />
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button colorScheme="blue" onClick={handleSubmit} isLoading={isLoading}>
            {checklistItem.id ? "Update" : "Create"}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
